import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MyWorkFilterService } from '../my-work-filter.service';
import { CapabilityRegistryService } from './capability-registry.service';
import { ConciergeUsageService } from './concierge-usage.service';
import {
  CapabilityContext,
  ConciergeMessage,
  NavigatorDecision,
} from './capability.types';

/**
 * Orchestrates the concierge: takes the conversation, asks the resolver (LLM in
 * Phase 2, a client-side keyword stub in Phase 1) which capability the user
 * wants, validates the answer against the registry, and runs the capability's
 * execute().
 *
 * Phase-2 seam: resolve() POSTs to URL_CONCIERGE_RESOLVE when it's configured
 * and falls back to the local stub when it isn't — the same "endpoint not
 * configured" guard pattern ReportsService.generateAiReport uses. No flag-flip
 * cliff: an org without the endpoint keeps a working (if simpler) palette.
 */
@Injectable({ providedIn: 'root' })
export class NavigatorService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private myWorkFilter = inject(MyWorkFilterService);
  private dialog = inject(MatDialog);
  private registry = inject(CapabilityRegistryService);
  private usage = inject(ConciergeUsageService);

  /** Ask the resolver which capability the conversation points at. */
  async resolve(messages: ConciergeMessage[]): Promise<NavigatorDecision> {
    const url = (environment as any).URL_CONCIERGE_RESOLVE;
    if (url) {
      try {
        const decision = await firstValueFrom(
          this.http.post<NavigatorDecision>(url, {
            messages,
            capabilities: this.registry.getSerializableManifest(),
          }),
        );
        return this.sanitize(decision);
      } catch (err) {
        console.error('[Navigator] resolve endpoint failed, falling back to local match:', err);
        // Fall through to the local stub so the palette still works on error.
      }
    }
    return this.localResolve(messages);
  }

  /**
   * Run a capability by id. Returns true if it executed, false if the id is
   * unknown (defense against a hallucinated/stale id) — callers should surface
   * a "couldn't do that" message on false.
   */
  async execute(capabilityId: string | null, params?: Record<string, any>): Promise<boolean> {
    if (!capabilityId) return false;
    const capability = this.registry.get(capabilityId);
    if (!capability) {
      console.warn('[Navigator] unknown capabilityId, refusing to execute:', capabilityId);
      return false;
    }
    // Never execute a capability the current user/org isn't allowed to see.
    if (!this.registry.getEnabled().some((c) => c.id === capabilityId)) {
      console.warn('[Navigator] capability not enabled for this user/org:', capabilityId);
      return false;
    }
    await capability.execute(this.buildContext(), params);
    // Single choke point for every path (palette click, keyboard Enter, clarify
    // pick, LLM auto-nav) — tally here so the Frequently-Used section stays in
    // sync without each caller remembering to. Only ids that actually ran count.
    this.usage.record(capabilityId);
    return true;
  }

  private buildContext(): CapabilityContext {
    return { router: this.router, myWorkFilter: this.myWorkFilter, dialog: this.dialog };
  }

  /** Drop any id the backend returned that isn't a real, enabled capability. */
  private sanitize(decision: NavigatorDecision): NavigatorDecision {
    const enabledIds = new Set(this.registry.getEnabled().map((c) => c.id));
    const capabilityId =
      decision.capabilityId && enabledIds.has(decision.capabilityId) ? decision.capabilityId : null;
    const candidates = (decision.candidates ?? []).filter((c) => enabledIds.has(c.capabilityId));
    return {
      ...decision,
      capabilityId,
      candidates,
      confidence: capabilityId ? decision.confidence : 0,
    };
  }

  /**
   * Phase-1 client-side stub: emulates the backend contract using the registry's
   * keyword search over the LAST user message. Deterministic and offline.
   */
  private localResolve(messages: ConciergeMessage[]): NavigatorDecision {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    const query = lastUser?.content ?? '';
    const matches = this.registry.search(query);

    if (matches.length === 0) {
      return {
        capabilityId: null,
        message: `I couldn't find a place for "${query.trim()}". Try rephrasing, or pick from the list.`,
        needsClarification: false,
        confidence: 0,
      };
    }

    const top = matches[0];
    const runnerUp = matches[1];
    // Map keyword score → a confidence-ish number. An exact phrase hit (>=100)
    // reads as high confidence; token-only hits read as medium.
    const confidence = Math.min(0.98, top.score >= 100 ? 0.9 + Math.min(0.08, top.score / 2000) : 0.4 + top.score / 60);

    // Treat near-ties as ambiguous so the UI offers a choice instead of guessing.
    const ambiguous = !!runnerUp && top.score - runnerUp.score <= 4 && top.score < 100;

    return {
      capabilityId: top.capability.id,
      message: ambiguous
        ? 'Did you mean one of these?'
        : `Taking you to ${top.capability.title}.`,
      needsClarification: ambiguous,
      confidence: ambiguous ? Math.min(confidence, 0.6) : confidence,
      candidates: matches.slice(0, 3).map((m) => ({
        capabilityId: m.capability.id,
        confidence: Math.min(0.98, m.score >= 100 ? 0.95 : 0.4 + m.score / 60),
      })),
    };
  }
}
